import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, ListIcon, UsersIcon, MenuIcon, PlayIcon } from '../icons';
import type { BlockingSectionViewportDirection } from '../../hooks/useBlockingSectionViewportDirection';
import type { GuidanceSectionId } from '../../lib/newPracticePageGuidance';
import { practiceModeLabel } from '../../lib/practiceModeLabel';
import { personaTypeLabel } from '../../lib/personaTypeLabel';

export interface SelectedPersonaInfo {
  name: string;
  type: string;
}

export interface NewPracticeBottomBarProps {
  selectedPersonas: SelectedPersonaInfo[];
  timeLimitMinutes: number;
  mode: 'verbal' | 'text';
  sessionTiming: 'now' | 'schedule';
  ctaDisabled: boolean;
  ctaLabel: string;
  mandatoryIncomplete: number | null;
  practiceUsageReadonly: boolean;
  loading: boolean;
  blockingViewportDirection: BlockingSectionViewportDirection;
  onCtaClick: () => void;
  onChipClick: (section: GuidanceSectionId | 'time' | 'mode') => void;
  onOpenMenu: () => void;
}

function SummaryChip({
  label,
  ok,
  onClick,
  testId,
  ariaLabel,
}: {
  label: string;
  ok: boolean;
  onClick?: () => void;
  testId?: string;
  ariaLabel?: string;
}) {
  return (
    <span
      role="status"
      data-testid={testId}
      onClick={onClick}
      aria-label={ariaLabel}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors shrink-0 cursor-pointer ${
        ok
          ? 'bg-green-50 text-green-800 border-green-200'
          : 'bg-amber-50 text-amber-900 border-amber-200'
      }`}
    >
      {label}
    </span>
  );
}

const NAV_ITEMS = [
  { to: '/', label: 'Home', icon: <HomeIcon />, tour: 'mobile-nav-home' },
  { to: '/practice', label: 'Sessions', icon: <ListIcon />, tour: 'mobile-nav-sessions' },
  { to: '/personas', label: 'Personas', icon: <UsersIcon />, tour: 'mobile-nav-personas' },
];

export function NewPracticeBottomBar({
  selectedPersonas,
  timeLimitMinutes,
  mode,
  sessionTiming,
  ctaDisabled,
  ctaLabel,
  mandatoryIncomplete,
  practiceUsageReadonly,
  loading,
  blockingViewportDirection,
  onCtaClick,
  onChipClick,
  onOpenMenu,
}: NewPracticeBottomBarProps) {
  const location = useLocation();
  const hasPersona = selectedPersonas.length > 0;
  const personaLabel = hasPersona
    ? selectedPersonas.length === 1
      ? `${selectedPersonas[0].name} (${personaTypeLabel(selectedPersonas[0].type)})`
      : `${selectedPersonas.length} personas`
    : 'Persona: --';

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  const badgeContent = loading
    ? '…'
    : practiceUsageReadonly
      ? '!'
      : mandatoryIncomplete != null && mandatoryIncomplete > 0
        ? mandatoryIncomplete
        : '✓';

  const ctaAriaLabel = loading
    ? 'Loading practice options'
    : practiceUsageReadonly
      ? 'Practice session limit reached for this month'
      : mandatoryIncomplete != null && mandatoryIncomplete > 0
        ? `${mandatoryIncomplete} required ${mandatoryIncomplete === 1 ? 'item' : 'items'} left. ${ctaLabel}.${
            blockingViewportDirection === 'down'
              ? ' Next required section is below the visible area.'
              : blockingViewportDirection === 'up'
                ? ' Next required section is above the visible area.'
                : ''
          }`
        : `${ctaLabel}. Ready to submit.`;

  return (
    <div
      data-tour="np-cta"
      className="fixed bottom-0 left-0 right-0 md:hidden z-40 safe-area-bottom"
    >
      {/* Summary chips strip */}
      <div
        data-testid="new-practice-summary-strip"
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border-t border-gray-200 overflow-x-auto"
      >
        <SummaryChip
          label={personaLabel}
          ok={hasPersona}
          onClick={() => onChipClick('persona')}
          testId="new-practice-chip-persona"
        />
        <SummaryChip
          label={`${timeLimitMinutes} min`}
          ok
          onClick={() => onChipClick('time')}
          testId="new-practice-chip-time"
        />
        <SummaryChip
          label={practiceModeLabel(mode)}
          ok
          onClick={() => onChipClick('mode')}
          testId="new-practice-chip-mode"
        />
        <SummaryChip
          label={sessionTiming === 'now' ? 'Now' : 'Scheduled'}
          ok={
            sessionTiming === 'now' ||
            (mandatoryIncomplete != null &&
              (hasPersona ? mandatoryIncomplete === 0 : mandatoryIncomplete <= 1))
          }
          onClick={() => onChipClick('schedule')}
          testId="new-practice-chip-timing"
        />
      </div>

      {/* Bottom nav with repurposed center button */}
      <nav className="bg-white border-t border-gray-200" aria-label="Mobile navigation">
        <div className="flex items-center justify-around h-16 px-1">
          {/* Home */}
          <Link
            to={NAV_ITEMS[0].to}
            className={`flex flex-col items-center justify-center flex-1 py-1 ${
              isActive(NAV_ITEMS[0].to) ? 'text-green-700' : 'text-gray-500'
            }`}
            data-tour={NAV_ITEMS[0].tour}
          >
            {NAV_ITEMS[0].icon}
            <span
              className={`text-[10px] mt-0.5 ${isActive(NAV_ITEMS[0].to) ? 'font-semibold' : 'font-medium'}`}
            >
              {NAV_ITEMS[0].label}
            </span>
          </Link>

          {/* Sessions */}
          <Link
            to={NAV_ITEMS[1].to}
            className={`flex flex-col items-center justify-center flex-1 py-1 ${
              isActive(NAV_ITEMS[1].to) ? 'text-green-700' : 'text-gray-500'
            }`}
            data-tour={NAV_ITEMS[1].tour}
          >
            {NAV_ITEMS[1].icon}
            <span
              className={`text-[10px] mt-0.5 ${isActive(NAV_ITEMS[1].to) ? 'font-semibold' : 'font-medium'}`}
            >
              {NAV_ITEMS[1].label}
            </span>
          </Link>

          {/* Center CTA button (repurposed "New") */}
          <button
            type="button"
            data-testid="new-practice-fab"
            aria-label={ctaAriaLabel}
            aria-busy={loading}
            onClick={onCtaClick}
            className="flex flex-col items-center justify-center -mt-4"
          >
            <div
              className={`relative w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-colors ${
                ctaDisabled ? 'bg-gray-200 text-gray-600' : 'bg-green-700 text-white'
              }`}
            >
              <PlayIcon className="w-6 h-6" />
              <span
                data-testid="new-practice-fab-badge"
                className={`absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold px-1 ${
                  ctaDisabled
                    ? mandatoryIncomplete != null && mandatoryIncomplete > 0
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-500 text-white'
                    : 'bg-white text-green-800 ring-1 ring-green-200'
                }`}
              >
                {mandatoryIncomplete != null && mandatoryIncomplete > 0 ? (
                  <span data-testid="new-practice-fab-badge-count">{mandatoryIncomplete}</span>
                ) : (
                  badgeContent
                )}
              </span>
            </div>
            <span
              className={`text-[10px] font-semibold mt-0.5 ${
                ctaDisabled ? 'text-gray-500' : 'text-green-700'
              }`}
            >
              {ctaDisabled
                ? mandatoryIncomplete != null && mandatoryIncomplete > 0
                  ? `${mandatoryIncomplete} left`
                  : 'Start'
                : 'Start'}
            </span>
          </button>

          {/* Personas */}
          <Link
            to={NAV_ITEMS[2].to}
            className={`flex flex-col items-center justify-center flex-1 py-1 ${
              isActive(NAV_ITEMS[2].to) ? 'text-green-700' : 'text-gray-500'
            }`}
            data-tour={NAV_ITEMS[2].tour}
          >
            {NAV_ITEMS[2].icon}
            <span
              className={`text-[10px] mt-0.5 ${isActive(NAV_ITEMS[2].to) ? 'font-semibold' : 'font-medium'}`}
            >
              {NAV_ITEMS[2].label}
            </span>
          </Link>

          {/* More */}
          <button
            type="button"
            onClick={onOpenMenu}
            className="flex flex-col items-center justify-center flex-1 py-1 text-gray-500"
            data-tour="mobile-nav-more"
          >
            <MenuIcon className="w-6 h-6" />
            <span className="text-[10px] mt-0.5 font-medium">More</span>
          </button>
        </div>
      </nav>
    </div>
  );
}

/**
 * Desktop-only sticky bottom bar for /practice/new. Shown via md:flex,
 * hidden on mobile where NewPracticeBottomBar handles the bottom area.
 */
export function NewPracticeDesktopBar({
  selectedPersonas,
  timeLimitMinutes,
  mode,
  sessionTiming,
  ctaDisabled,
  ctaLabel,
  mandatoryIncomplete,
  practiceUsageReadonly,
  loading,
  onCtaClick,
  onChipClick,
}: Pick<
  NewPracticeBottomBarProps,
  | 'selectedPersonas'
  | 'timeLimitMinutes'
  | 'mode'
  | 'sessionTiming'
  | 'ctaDisabled'
  | 'ctaLabel'
  | 'mandatoryIncomplete'
  | 'practiceUsageReadonly'
  | 'loading'
  | 'onCtaClick'
  | 'onChipClick'
>) {
  const hasPersona = selectedPersonas.length > 0;
  const personaLabel = hasPersona
    ? selectedPersonas.length === 1
      ? `${selectedPersonas[0].name} (${personaTypeLabel(selectedPersonas[0].type)})`
      : `${selectedPersonas.length} personas`
    : 'Persona: --';

  return (
    <div
      data-tour="np-cta"
      data-testid="new-practice-desktop-bar"
      className="hidden md:flex sticky bottom-0 z-30 items-center gap-3 px-4 py-2.5 bg-white border-t border-gray-200 -mx-4 sm:-mx-6 lg:-mx-8"
    >
      <div className="flex items-center gap-1.5 flex-1 overflow-x-auto min-w-0">
        <SummaryChip label={personaLabel} ok={hasPersona} onClick={() => onChipClick('persona')} />
        <SummaryChip label={`${timeLimitMinutes} min`} ok onClick={() => onChipClick('time')} />
        <SummaryChip label={practiceModeLabel(mode)} ok onClick={() => onChipClick('mode')} />
        <SummaryChip
          label={sessionTiming === 'now' ? 'Now' : 'Scheduled'}
          ok={
            sessionTiming === 'now' ||
            (mandatoryIncomplete != null &&
              (hasPersona ? mandatoryIncomplete === 0 : mandatoryIncomplete <= 1))
          }
          onClick={() => onChipClick('schedule')}
        />
      </div>
      <button
        type="button"
        data-testid="new-practice-desktop-cta"
        onClick={onCtaClick}
        disabled={loading}
        className={`flex items-center gap-2 h-10 px-5 rounded-full font-semibold text-sm transition-colors shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 ${
          ctaDisabled
            ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            : 'bg-green-700 text-white hover:bg-green-800'
        }`}
      >
        <span>{ctaLabel}</span>
        <span
          data-testid="new-practice-desktop-cta-badge"
          className={`flex items-center justify-center min-w-[1.25rem] h-5 rounded-full px-1 text-[11px] font-bold ${
            ctaDisabled
              ? mandatoryIncomplete != null && mandatoryIncomplete > 0
                ? 'bg-amber-500 text-white'
                : 'bg-gray-500 text-white'
              : 'bg-white text-green-800'
          }`}
        >
          {loading ? (
            '…'
          ) : practiceUsageReadonly ? (
            '!'
          ) : mandatoryIncomplete != null && mandatoryIncomplete > 0 ? (
            <span data-testid="new-practice-desktop-cta-badge-count">{mandatoryIncomplete}</span>
          ) : (
            '✓'
          )}
        </span>
      </button>
    </div>
  );
}
